import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Button,
  Tooltip,
  Drawer,
  Badge,
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  TableOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  UnorderedListOutlined,
  ToolOutlined,
  FireOutlined,
  CalendarOutlined,
  WalletOutlined,
  SunOutlined,
  MoonOutlined,
  DesktopOutlined,
  ShopOutlined,
  DollarOutlined,
  DownOutlined,
  NotificationOutlined,
  EllipsisOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';
import { addNotification } from '../../store/slices/notificationsSlice';
import { useResponsive } from '../../hooks/useResponsive';
import { useOfflineCatalogWarmup } from '../../hooks/useOfflineCatalogWarmup';
import {
  canAccessDashboard,
  canRegisterExpenses,
  canSeeAllExpenses,
  isAdmin,
  canOperateFacturas,
  isFloorStaff,
  isManager,
  isSupportUser,
  isChef,
} from '../../utils/roleHelpers';
import { useGetPendingShiftClosingsCountQuery } from '../../services/shiftClosingApi';
import { useGetChangelogUnseenCountQuery } from '../../services/changelogApi';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useGetSubscriptionStatusQuery } from '../../services/stripeApi';
import { useIsModuleEnabled } from '../../hooks/useIsModuleEnabled';
import { useHubEvent } from '../../hooks/useHubEvent';
import { PENDING_SHIFT_CLOSINGS_CHANGED_EVENT } from '../../services/realtimeHub';
import { MODULE_KEYS } from '../../constants/moduleKeys';
import { useGetUserLimitInfoQuery } from '../../services/usersApi';
import NotificationsPanel from '../notifications/NotificationsPanel';
import OrderReadyListener from '../notifications/OrderReadyListener';
import PrintStatusListener from '../notifications/PrintStatusListener';
import PrintAnimationOverlay from '../printing/PrintAnimationOverlay';
import CashRegisterStatusAlert from '../cashRegister/CashRegisterStatusAlert';
import ConnectionBanner from './ConnectionBanner';
import OfflineQueueNotice from '../offline/OfflineQueueNotice';
import PushNotificationsToggle from '../notifications/PushNotificationsToggle';
import TrialExpiredModal from '../common/TrialExpiredModal';
import ThemeToggle from './ThemeToggle';
import ProfileModal from './ProfileModal';
import RestaurantSwitcher from './RestaurantSwitcher';
import UserAvatar from '../common/UserAvatar';
import { useTheme } from '../../hooks/useTheme';
import { BottomEdgeProvider, useBottomEdgeOwners } from '../../hooks/useBottomEdge';
import AiChatWidget from '../aiChat/AiChatWidget';
import './layout.css';

const { Header, Sider, Content } = Layout;

interface MainLayoutProps {
  children: React.ReactNode;
}

// POS-operational routes where screen space matters most: the sidebar
// auto-collapses to the icon rail on entry and restores on exit. A manual
// toggle while on one of these routes wins until the user navigates off.
const OPERATIONAL_ROUTES = ['/tables', '/new-order', '/kitchen', '/cash-register'];

// Pantallas de flujo que son DUEÑAS del borde inferior: traen su propio footer
// fijo con la acción principal, así que la tab bar de modo piso se oculta ahí o
// le taparía el botón en teléfono.
//
// Lista aparte de OPERATIONAL_ROUTES aunque se traslapen: esa dice "aquí conviene
// colapsar el sidebar" e incluye /tables, que sí es un destino de primer nivel y
// tiene que conservar su tab bar. Confundirlas dejaría a Mesas sin navegación.
// Toda pantalla nueva con footer fijo (cobro, división de cuenta) va aquí.
const BOTTOM_EDGE_ROUTES = ['/new-order'];

const matchesRoute = (pathname: string, routes: string[]) =>
  routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));

// Destinos que caben en la navegación de modo piso, dock y tab bar por igual:
// cuatro es lo que entra cómodo en el ancho de un teléfono. Una sola constante
// para los dos porque es la misma política; sólo difieren en a dónde mandan el
// excedente (menú "Más" en el dock, drawer en la tab bar).
const FLOOR_NAV_SLOTS = 4;

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  // Una pantalla hija puede reclamar el borde inferior (barra de acciones fija).
  // El estado vive aquí porque este componente es quien decide si pinta la tab bar.
  const { owned: bottomEdgeOwned, claim: claimBottomEdge } = useBottomEdgeOwners();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, trialEndDate, isAuthenticated } = useAppSelector((state) => state.auth);
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  // Solo hace algo en la caja offline: deja la carta guardada para abrir sin red.
  useOfflineCatalogWarmup();
  const { isMobile, isTablet } = useResponsive();
  const { mode: themeMode, changeTheme } = useTheme();

  // Modo piso: el personal operativo (Mesero, Cajero, Cajero/Mesero, Chef) ve
  // 2-5 destinos; un sidebar de 200px para eso es chrome muerto en tablet. En
  // su lugar: dock en el header (desktop/tablet) y tab bar inferior (móvil).
  // Solo cambia el chrome — rutas, menú filtrado y permisos son los mismos.
  const floorMode = isFloorStaff(user?.role) && !isSupportUser(user?.email);

  // Sidebar auto-collapse on operational routes. autoCollapsed tracks that WE
  // collapsed it (so we only auto-expand what we auto-collapsed, preserving a
  // user's own collapsed preference); manualOverride tracks a user toggle
  // while on an operational route (so we stop fighting them there).
  const isOperationalRoute = matchesRoute(location.pathname, OPERATIONAL_ROUTES);
  const isTablesRoute = matchesRoute(location.pathname, ['/tables']);
  const sidebarAutoCollapsedRef = useRef(false);
  const sidebarManualOverrideRef = useRef(false);

  useEffect(() => {
    if (isMobile || floorMode) return;
    if (isOperationalRoute) {
      if (!sidebarManualOverrideRef.current && !collapsed) {
        sidebarAutoCollapsedRef.current = true;
        setCollapsed(true);
      }
    } else {
      sidebarManualOverrideRef.current = false;
      if (sidebarAutoCollapsedRef.current) {
        sidebarAutoCollapsedRef.current = false;
        setCollapsed(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOperationalRoute, isMobile]);

  // Get subscription status to determine if sidebar should be hidden
  const { data: subscriptionStatus } = useGetSubscriptionStatusQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: limitInfo } = useGetUserLimitInfoQuery(undefined, {
    skip: !isAuthenticated,
  });

  // Check if user has active subscription or valid trial
  const hasActiveSubscription = subscriptionStatus?.hasSubscription &&
    (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing');
  const trialEndDateValue = subscriptionStatus?.trialEnd || limitInfo?.trialEndDate || trialEndDate;
  const isTrialStillValid = trialEndDateValue && new Date(trialEndDateValue) > new Date();

  // Hide sidebar if no active subscription and trial expired
  const shouldHideSidebar = !hasActiveSubscription && !isTrialStillValid && subscriptionStatus !== undefined;

  // Get restaurant settings (including logo)
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();

  // Get facturacion setting (to show/hide facturacion menu)
  const { data: facturacionSetting } = useGetBoolSettingQuery('FacturacionActiva');
  const isFacturacionEnabled = facturacionSetting?.value ?? false;

  // Inventario, Finanzas and AsistenteIA are Pro-bundle modules — enabled when the
  // per-tenant setting is on, OR the organization is on the Profesional plan. Same
  // rule the backend [RequireModule] filter applies. Facturacion and MenuDelDia stay
  // setting-only (Pro plan does not auto-enable them).
  const { enabled: isInventarioEnabled } = useIsModuleEnabled(MODULE_KEYS.Inventario);
  const { enabled: isFinanzasEnabled } = useIsModuleEnabled(MODULE_KEYS.Finanzas);
  const { enabled: isAiChatEnabled } = useIsModuleEnabled(MODULE_KEYS.AsistenteIA);

  // Menu del dia setting (setting-only — no plan bypass)
  const { data: menuDelDiaSetting } = useGetBoolSettingQuery('MenuDelDiaActivo');
  const isMenuDelDiaEnabled = menuDelDiaSetting?.value ?? false;

  // Cierre de Turno setting — hidden by default. Controls the "Cierres de Turno"
  // sidebar item and the shift-close button in the cash register.
  const { data: cierreTurnoSetting } = useGetBoolSettingQuery('CierreTurnoActivo');
  const isCierreTurnoEnabled = cierreTurnoSetting?.value ?? false;

  // Track previous pending count to detect changes
  const previousPendingCountRef = useRef<number>(0);

  // Get pending shift closings count. Tiempo real: el evento SignalR
  // PendingShiftClosingsChanged refetchea al crear/revisar un corte; el polling
  // queda como fallback lento por si el websocket se cae.
  const shiftClosingsBadgeEnabled =
    isCierreTurnoEnabled && (isAdmin(user?.role) || isManager(user?.role));
  const { data: pendingCount, refetch: refetchPendingCount } = useGetPendingShiftClosingsCountQuery(undefined, {
    pollingInterval: 60000,
    skip: !shiftClosingsBadgeEnabled,
  });
  useHubEvent(
    PENDING_SHIFT_CLOSINGS_CHANGED_EVENT,
    () => refetchPendingCount(),
    shiftClosingsBadgeEnabled
  );

  // "Novedades" unseen count for the sidebar badge. Management roles only —
  // same audience as the menu item. Entries are published rarely (support,
  // via admin-panel), so slow polling is plenty; opening the page marks them
  // seen and invalidates this query, clearing the badge live.
  const changelogBadgeEnabled =
    isSupportUser(user?.email) || isAdmin(user?.role) || isManager(user?.role);
  const { data: changelogUnseen } = useGetChangelogUnseenCountQuery(undefined, {
    pollingInterval: 300000,
    skip: !changelogBadgeEnabled,
  });

  // Add notification when pending shift closings count changes
  useEffect(() => {
    if (pendingCount && pendingCount.count > 0) {
      // Only add notification if count increased from previous value
      if (pendingCount.count > previousPendingCountRef.current) {
        dispatch(addNotification({
          type: 'warning',
          title: 'Cierres de Turno Pendientes',
          message: `Hay ${pendingCount.count} cierre(s) de turno esperando revisión`,
          actionUrl: '/shift-closings',
        }));
      }
      // Update ref with current count
      previousPendingCountRef.current = pendingCount.count;
    } else if (pendingCount?.count === 0) {
      // Reset ref when count goes to 0
      previousPendingCountRef.current = 0;
    }
  }, [pendingCount, dispatch]);

  // Menu keys match the first path segment; the exceptions are aliased here.
  const getCurrentMenuKey = () => {
    const path = location.pathname;
    if (path.startsWith('/new-order')) return 'orders';
    if (path.startsWith('/admin/cash-registers')) return 'admin/cash-registers';
    return path.split('/')[1] || 'dashboard';
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  // Check if user is admin for subscription menu access
  const userIsAdmin = isAdmin(user?.role) || isSupportUser(user?.email);

  const userMenuItems = [
    // User info header — the trigger shows only avatar + name + role tag;
    // full role and email live here on every breakpoint.
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0', minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {user?.firstName} {user?.lastName}
          </div>
          <div style={{ color: 'var(--text-link)', fontSize: 12, marginTop: 2 }}>
            {user?.role}
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 11, marginTop: 2 }}>
            {user?.email}
          </div>
        </div>
      ),
      disabled: true,
      style: { cursor: 'default' },
    },
    {
      type: 'divider' as const,
    },
    // Mi perfil: el avatar es lo único que cada quien puede cambiarse a sí
    // mismo, y vive aquí porque es donde ya se ve su propio chip.
    {
      key: 'profile',
      icon: <IdcardOutlined />,
      label: 'Mi perfil',
    },
    {
      type: 'divider' as const,
    },
    // Theme selector - Only on mobile (desktop has button in header)
    ...(isMobile ? [{
      key: 'theme',
      icon: themeMode === 'dark' ? <MoonOutlined /> : themeMode === 'light' ? <SunOutlined /> : <DesktopOutlined />,
      label: 'Tema',
      children: [
        {
          key: 'theme-light',
          icon: <SunOutlined />,
          label: themeMode === 'light' ? '✓ Claro' : 'Claro',
          onClick: () => changeTheme('light'),
        },
        {
          key: 'theme-dark',
          icon: <MoonOutlined />,
          label: themeMode === 'dark' ? '✓ Oscuro' : 'Oscuro',
          onClick: () => changeTheme('dark'),
        },
        {
          key: 'theme-system',
          icon: <DesktopOutlined />,
          label: themeMode === 'system' ? '✓ Sistema' : 'Sistema',
          onClick: () => changeTheme('system'),
        },
      ],
    },
    {
      type: 'divider' as const,
    }] : []),
    // Settings - Only on mobile for admins (desktop has button in header)
    ...(isMobile && userIsAdmin ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Configuración',
    },
    {
      type: 'divider' as const,
    }] : []),
    // Subscription management - Only for admins
    ...(userIsAdmin ? [{
      key: 'subscription',
      icon: <WalletOutlined />,
      label: 'Administrar Suscripción',
    },
    {
      type: 'divider' as const,
    }] : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Cerrar Sesión',
      onClick: handleLogout,
    },
  ];

  // Check if user is support user (for system settings access)
  const isSupport = isSupportUser(user?.email);

  // Filter menu items based on user role
  const getMenuItems = () => {
    const userRole = user?.role;
    // Support users always have full admin access
    const hasAdminAccess = isSupport || isAdmin(userRole) || isManager(userRole);
    const hasDashboardAccess = isSupport || canAccessDashboard(userRole);

    // Check if user has Cajero role (for cash register access)
    // Support users also have full access
    const hasCajeroRole = isSupport
                       || userRole?.toLowerCase() === 'cajero'
                       || userRole?.toLowerCase() === 'cajero/mesero'
                       || userRole?.toLowerCase() === 'administrador'
                       || userRole?.toLowerCase() === 'admin'
                       || userRole?.toLowerCase() === 'administrator';

    // Check if user can see Orders list (includes cajero/mesero and mesero for TakeOut/Delivery)
    const canSeeOrdersList = isSupport
                          || userRole?.toLowerCase() === 'cajero'
                          || userRole?.toLowerCase() === 'cajero/mesero'
                          || userRole?.toLowerCase() === 'mesero'
                          || userRole?.toLowerCase() === 'administrador'
                          || userRole?.toLowerCase() === 'admin'
                          || userRole?.toLowerCase() === 'administrator'
                          || userRole?.toLowerCase() === 'gerente'
                          || userRole?.toLowerCase() === 'manager';

    // Check if user can see Kitchen display (Chef role or Admin)
    const canSeeKitchen = isSupport
                        || isChef(userRole)
                        || hasAdminAccess;

    // Items grouped into labeled sections (Operación / Administración / Análisis).
    // A group only renders when it has visible items for this role, so e.g. a
    // waiter sees just Operación with no stray labels.
    const operationItems = [
      // Dashboard - Only for Administrador and Gerente
      ...(hasDashboardAccess ? [{
        key: 'dashboard',
        icon: <HomeOutlined />,
        label: 'Dashboard',
      }] : []),
      // Orders - includes cajero/mesero and mesero for TakeOut/Delivery
      ...(canSeeOrdersList ? [{
        key: 'orders',
        icon: <UnorderedListOutlined />,
        label: 'Órdenes',
      }] : []),
      // Tables - Accessible to all roles
      {
        key: 'tables',
        icon: <TableOutlined />,
        label: 'Mesas',
      },
      // Kitchen Display - For Chef role and Admin
      ...(canSeeKitchen ? [{
        key: 'kitchen',
        icon: <FireOutlined />,
        label: 'Cocina',
      }] : []),
      // Menú del Día - Only for Admin and Manager, and only if MenuDelDiaActivo is enabled
      ...(hasAdminAccess && isMenuDelDiaEnabled ? [{
        key: 'daily-menus',
        icon: <CalendarOutlined />,
        label: 'Menú del Día',
      }] : []),
    ];

    const adminItems = [
      // Cash Register - Only visible for users with Cajero role or Admin.
      // Single "Caja" entry; "Mi Caja" / "Todas las Cajas" are tabs inside the page.
      ...(hasCajeroRole ? [{
        key: 'cash-register',
        icon: <CreditCardOutlined />,
        label: 'Caja',
      }] : []),
      // Shift Closings - Admin/Manager + CierreTurnoActivo. The badge surfaces the
      // pending-review count that was already being polled for notifications.
      ...(hasAdminAccess && isCierreTurnoEnabled ? [{
        key: 'shift-closings',
        icon: <CheckCircleOutlined />,
        label: (
          <span className="menu-label-badged">
            <span className="menu-label-text">Cierres de Turno</span>
            {(pendingCount?.count ?? 0) > 0 && (
              <Badge count={pendingCount!.count} size="small" />
            )}
          </span>
        ),
      }] : []),
      // Facturación - quien opera CFDI (Admin/Gerente/Cajero; espejo de FacturacionRoles.Operate)
      // + FacturacionActiva. Antes dependía de hasCajeroRole y el Gerente no la veía.
      ...(canOperateFacturas(userRole, isSupport) && isFacturacionEnabled ? [{
        key: 'facturas',
        icon: <FileTextOutlined />,
        label: 'Facturación',
      }] : []),
      // Inventario - Admin/Manager + módulo Inventario
      ...(hasAdminAccess && isInventarioEnabled ? [{
        key: 'inventory',
        icon: <ShopOutlined />,
        label: 'Inventario',
      }] : []),
      // Finanzas - todo el que puede registrar un gasto + módulo Finanzas.
      //
      // El personal de piso y caja entra a la MISMA ruta, pero la pantalla que recibe es
      // sólo sus gastos (el backend recorta las filas y omite los totales). Por eso la
      // etiqueta cambia: llamarle "Finanzas" a una lista de cuatro gastos propios promete
      // un módulo que no van a encontrar.
      ...(canRegisterExpenses(userRole, isSupport) && isFinanzasEnabled ? [{
        key: 'finanzas',
        icon: <DollarOutlined />,
        label: canSeeAllExpenses(userRole, isSupport) ? 'Finanzas' : 'Mis gastos',
      }] : []),
    ];

    const analysisItems = [
      // Reports - Only for Administrador and Gerente
      ...(hasDashboardAccess ? [{
        key: 'reports',
        icon: <BarChartOutlined />,
        label: 'Reportes',
      }] : []),
      // Novedades - Admin/Manager. Permanent changelog + manuals library;
      // the badge counts published entries this user hasn't seen yet.
      ...(hasAdminAccess ? [{
        key: 'novedades',
        icon: <NotificationOutlined />,
        label: (
          <span className="menu-label-badged">
            <span className="menu-label-text">Novedades</span>
            {(changelogUnseen?.count ?? 0) > 0 && (
              <Badge count={changelogUnseen!.count} size="small" />
            )}
          </span>
        ),
      }] : []),
      // System Settings - Only for support users (@massresto.com)
      ...(isSupport ? [{
        key: 'system-settings',
        icon: <ToolOutlined />,
        label: 'Sistema (Soporte)',
      }] : []),
    ];

    return [
      ...(operationItems.length > 0 ? [{
        type: 'group' as const,
        key: 'grp-operacion',
        label: 'Operación',
        children: operationItems,
      }] : []),
      ...(adminItems.length > 0 ? [{
        type: 'group' as const,
        key: 'grp-admin',
        label: 'Administración',
        children: adminItems,
      }] : []),
      ...(analysisItems.length > 0 ? [{
        type: 'group' as const,
        key: 'grp-analisis',
        label: 'Análisis',
        children: analysisItems,
      }] : []),
    ];
  };

  const menuItems = getMenuItems();

  // Modo piso: el dock/tab bar se alimenta del MISMO menú filtrado por rol,
  // aplanando los grupos (un cajero no necesita rótulos de sección para 4
  // destinos). Dock: 4 + "Más"; tabs móviles: 3 + "Más" (abre el drawer).
  type FloorItem = { key: string; icon?: React.ReactNode; label?: React.ReactNode };
  const flatMenuItems: FloorItem[] = floorMode
    ? (menuItems as Array<{ children?: FloorItem[] }>).flatMap((g) => g.children ?? [])
    : [];
  const dockItems = flatMenuItems.slice(0, FLOOR_NAV_SLOTS);
  const dockOverflow = flatMenuItems.slice(FLOOR_NAV_SLOTS);

  // Misma política de slots que el dock, distinta afordancia de desborde: el
  // dock manda el excedente a un menú y la tab bar al drawer. Si el rol tiene
  // 4 destinos o menos van todos y NO se pinta "Más": un "Más" que abre un
  // cajón con las mismas opciones que ya están a la izquierda es un botón que
  // miente. Sólo cuando hay un quinto se cede el último lugar.
  const tabsOverflow = flatMenuItems.length > FLOOR_NAV_SLOTS;
  const tabItems = tabsOverflow
    ? flatMenuItems.slice(0, FLOOR_NAV_SLOTS - 1)
    : flatMenuItems;
  const currentMenuKey = getCurrentMenuKey();

  // El bloqueo por prueba vencida esconde el sidebar y el drawer, así que el
  // dock y la tab bar tienen que caer con ellos: si no, un rol de piso conserva
  // la navegación completa que el bloqueo le quitaba a gestión.
  const floorNavEnabled = floorMode && !shouldHideSidebar;

  // La tab bar solo vive en los destinos de primer nivel: las pantallas dueñas
  // del borde inferior traen su propio footer fijo y chocarían con ella. Eso
  // incluye a las que se montan por estado dentro de una ruta (la gestión de
  // una mesa abierta vive en /tables), por eso no basta con mirar la URL.
  const showFloorTabbar =
    isMobile &&
    floorNavEnabled &&
    !bottomEdgeOwned &&
    !matchesRoute(location.pathname, BOTTOM_EDGE_ROUTES) &&
    flatMenuItems.some((item) => item.key === currentMenuKey);

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(`/${key}`);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = () => {
    if (isMobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      if (isOperationalRoute) {
        sidebarManualOverrideRef.current = true;
      }
      setCollapsed(!collapsed);
    }
  };

  const MenuComponent = () => (
    <Menu
      theme="light"
      mode="inline"
      selectedKeys={[getCurrentMenuKey()]}
      style={{ borderRight: 0 }}
      items={menuItems}
      onClick={handleMenuClick}
    />
  );

  // Tenant identity, split across the frame: the restaurant's LOGO crowns the
  // sidebar (initial-avatar fallback when no logo is uploaded) and its NAME
  // lives in the header. logoOnly renders just the mark (sidebar); the full
  // variant (logo + name) is used in the mobile drawer title.
  const tenantLogo = restaurantSettings?.logoBase64 && restaurantSettings?.logoMimeType
    ? `data:${restaurantSettings.logoMimeType};base64,${restaurantSettings.logoBase64}`
    : undefined;
  // La marca del tenant, en un solo lugar: el logo si lo subió, y si no su
  // inicial sobre teal. Vive aparte porque se pinta en tres tamaños —sidebar
  // expandido, sidebar colapsado y header móvil— y tener una copia por tamaño
  // hacía que cualquier cambio a las reglas de respaldo hubiera que aplicarlo
  // tres veces.
  const TenantMark = ({ size, className }: { size: number; className?: string }) => (
    <Avatar
      src={tenantLogo}
      size={size}
      shape="square"
      className={className}
      style={{
        // El radio acompaña al tamaño para que la esquina se vea igual de
        // redondeada en el rail de 40px y en el isotipo de 30px.
        borderRadius: Math.round(size * 0.2),
        flexShrink: 0,
        ...(tenantLogo
          ? {}
          : {
              backgroundColor: 'var(--color-primary)',
              fontWeight: 700,
              fontSize: Math.round(size * 0.45),
            }),
      }}
    >
      {currentRestaurant?.name?.charAt(0).toUpperCase()}
    </Avatar>
  );

  const TenantIdentity = ({ logoOnly, compact }: { logoOnly?: boolean; compact?: boolean }) => (
    <div className={`layout-tenant${logoOnly ? ' layout-tenant--logo-only' : ''}`}>
      <TenantMark size={compact ? 36 : 40} />
      {!logoOnly && (
        <span className="layout-tenant-name">{currentRestaurant?.name}</span>
      )}
    </div>
  );

  // Isotipo del tenant para el header móvil: el nombre completo se queda en
  // desktop. En una pantalla de teléfono esos ~150px compiten con el contenido,
  // y la marca del restaurante ubica igual de bien que su nombre escrito.
  const tenantMark = <TenantMark size={30} />;

  // Modo piso en tablet/desktop: los destinos del rol viven en el chrome, no en
  // un sidebar. Los que no caben (raro con 4-5 entradas) caen en "Más".
  const floorDock = floorNavEnabled && !isMobile ? (
    <nav className="floor-dock">
      {dockItems.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`floor-dock-item${item.key === currentMenuKey ? ' is-active' : ''}`}
          onClick={() => handleMenuClick({ key: item.key })}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
      {dockOverflow.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dockOverflow.map((item) => ({
              key: item.key,
              icon: item.icon,
              label: item.label,
            })),
            onClick: handleMenuClick,
          }}
        >
          <button type="button" className="floor-dock-item">
            <EllipsisOutlined />
            <span>Más</span>
          </button>
        </Dropdown>
      )}
    </nav>
  ) : null;

  // Modo piso en teléfono: barra inferior en la zona del pulgar. "Más" abre el
  // drawer con el menú completo del rol, así nada queda inalcanzable.
  const floorTabbar = showFloorTabbar ? (
    <nav className="floor-tabbar">
      {tabItems.map((item) => (
        <button
          key={item.key}
          type="button"
          className={`floor-tab${item.key === currentMenuKey ? ' is-active' : ''}`}
          onClick={() => handleMenuClick({ key: item.key })}
        >
          <span className="floor-tab-icon">{item.icon}</span>
          <span className="floor-tab-label">{item.label}</span>
        </button>
      ))}
      {tabsOverflow && (
        <button
          type="button"
          className="floor-tab"
          onClick={() => setMobileMenuOpen(true)}
        >
          <span className="floor-tab-icon"><EllipsisOutlined /></span>
          <span className="floor-tab-label">Más</span>
        </button>
      )}
    </nav>
  ) : null;

  return (
    <Layout style={{ minHeight: '100vh' }} className={floorMode ? 'layout--floor' : undefined}>
      {/* Trial Expired Modal - blocks entire UI when trial ends */}
      <TrialExpiredModal />

      {/* Conexión SignalR: alerta in-app cuando cocina marca una orden lista */}
      <OrderReadyListener />

      {/* Avisos de impresión (cloud print) sin bloquear la pantalla. Vive aquí y no en cada
          modal que imprime porque el que dispara la impresión suele cerrarse enseguida. */}
      <PrintStatusListener />

      {/* Impresora en pantalla al imprimir precuenta, cuenta o corte (setting AnimacionImpresion). */}
      <PrintAnimationOverlay />

      {/* Desktop Sidebar — solo gestión (Admin/Gerente/soporte); los roles de
          piso navegan con el dock del header. Hidden when trial expired. */}
      {!isMobile && !shouldHideSidebar && !floorMode && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          breakpoint="lg"
          collapsedWidth={isTablet ? 0 : 80}
          className="layout-sider"
          style={{
            background: 'var(--chrome-bg)',
            boxShadow: `2px 0 8px var(--shadow-color)`,
          }}
        >
          {/* The tenant's mark heads their system; ours is the footer */}
          <TenantIdentity logoOnly compact={collapsed} />

          <MenuComponent />

          <div className="layout-powered">
            {collapsed ? (
              <img src="/logo-icon.png" alt="Massrestaurant" />
            ) : (
              <>powered by <b>Massrestaurant</b></>
            )}
          </div>
        </Sider>
      )}

      {/* Mobile Drawer - Hidden when trial expired and no subscription */}
      {!shouldHideSidebar && (
        <Drawer
          title={<TenantIdentity />}
          placement="left"
          onClose={() => setMobileMenuOpen(false)}
          open={isMobile && mobileMenuOpen}
          bodyStyle={{ padding: 0 }}
          width={280}
          className="layout-mobile-menu"
          footer={
            <div className="layout-powered" style={{ border: 0, padding: 0 }}>
              powered by <b>Massrestaurant</b>
            </div>
          }
        >
          <MenuComponent />
        </Drawer>
      )}

      <Layout>
        <Header
          className={`layout-header${floorMode ? ' layout-header--floor' : ''}`}
          style={{
            padding: isMobile ? '0 12px' : '0 16px',
            background: floorMode ? 'var(--chrome-bg)' : 'var(--bg-primary)',
            boxShadow: `0 2px 8px var(--shadow-color)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Space align="center">
            {/* Toggle: solo cuando hay un menú lateral que abrir. En modo piso
                de escritorio navega el dock, y en teléfono la tab bar — salvo en
                pantallas de flujo, donde la tab bar se oculta y el drawer vuelve
                a ser la única salida. */}
            {!shouldHideSidebar && (!floorMode || (isMobile && !showFloorTabbar)) && (
              <Button
                type="text"
                className="layout-header-toggle"
                icon={
                  isMobile ?
                    <MenuUnfoldOutlined /> :
                    (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)
                }
                onClick={toggleMobileMenu}
              />
            )}

            {/* Identidad del tenant: nombre en escritorio, isotipo en móvil.
                Las dos ramas se condicionan igual: sin restaurante todavía
                cargado, el isotipo sin guarda dejaba un cuadro teal vacío de
                30×30 (la inicial sale del nombre, que aún no existe). Con 2+
                restaurantes accesibles, el nombre se vuelve switcher. */}
            {currentRestaurant &&
              (isMobile ? (
                <>
                  {tenantMark}
                  <RestaurantSwitcher compact />
                </>
              ) : (
                <RestaurantSwitcher />
              ))}

            {floorDock}
          </Space>

          <Space size={isMobile ? "small" : "middle"} align="center">
            {/* Push de "orden lista" — visible también en móvil (los meseros usan celular) */}
            <PushNotificationsToggle />

            {!isMobile && (
              <NotificationsPanel isMobile={isMobile} />
            )}

            {/* Theme toggle - desktop only (mobile has it in user menu) */}
            {!isMobile && (
              <ThemeToggle size="middle" />
            )}

            {/* Settings button - desktop only, admin only */}
            {!isMobile && userIsAdmin && (
              <Tooltip title="Configuración">
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  size="middle"
                  onClick={() => navigate('/configuracion')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              </Tooltip>
            )}

            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: ({ key }) => {
                  if (key === 'logout') {
                    handleLogout();
                  } else if (key === 'profile') {
                    setProfileModalOpen(true);
                  } else if (key === 'settings') {
                    navigate('/configuracion');
                  } else if (key === 'subscription') {
                    navigate('/subscription');
                  }
                },
              }}
              placement="bottomRight"
              arrow
            >
              <div className="layout-user-trigger">
                <UserAvatar
                  size={32}
                  firstName={user?.firstName}
                  email={user?.email}
                  avatarIcon={user?.avatarIcon}
                  // Sobre el chrome teal tinta, el teal de acción casi no se
                  // despega: en modo piso el disco va en blanco translúcido.
                  background={floorMode ? 'rgba(255, 255, 255, 0.18)' : undefined}
                />
                {!isMobile && (
                  <>
                    <span className="layout-user-name">
                      {user?.firstName} {user?.lastName}
                    </span>
                    {user?.role && (
                      <span className="layout-user-role">{user.role}</span>
                    )}
                    <DownOutlined className="layout-user-chevron" />
                  </>
                )}
              </div>
            </Dropdown>
          </Space>
        </Header>

        <Content
          className={showFloorTabbar ? 'layout-content--tabbar' : undefined}
          style={{
            margin: isMobile ? '4px' : '24px',
            padding: isMobile ? '8px' : '24px',
            background: 'var(--bg-primary)',
            borderRadius: '8px',
            minHeight: 280,
            overflow: 'auto',
          }}
        >
          <ConnectionBanner />
          <OfflineQueueNotice />
          <CashRegisterStatusAlert />
          {/* Las pantallas hijas reclaman el borde inferior desde aquí adentro
              con useOwnsBottomEdge, para que la tab bar se quite antes de que
              una barra de acciones fija se le encime. */}
          <BottomEdgeProvider claim={claimBottomEdge}>
            {children}
          </BottomEdgeProvider>
        </Content>

        {floorTabbar}
      </Layout>

      {/* Hidden on mobile in operational routes: the floating button overlaps
          the fixed bottom action bar (e.g. Precuenta in table management).
          Y oculto siempre en Mesas: es la pantalla de piso, el botón flotante
          se encima de la esquina del panel de la mesa y ahí no se consulta al
          asistente — se cobra. */}
      {canAccessDashboard(user?.role)
        && isAiChatEnabled
        && !isTablesRoute
        && !(isMobile && isOperationalRoute)
        && <AiChatWidget />}

      <ProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
    </Layout>
  );
};

export default MainLayout;